import { type ReactNode } from "react";

export const WIZARD_STEPS = ["Set up", "Route traffic", "Verify"] as const;

export type WizardStepIndex = 0 | 1 | 2;

/* The frame every wizard step sits in: heading, progress bar, step name and lead. One
 * component because the dashboard modal and /welcome show the same wizard. */
export function WizardFrame({
  step,
  lead,
  children,
}: {
  step: WizardStepIndex;
  lead: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex w-full flex-col">
      <h1
        className="t-h2"
        style={{
          color: "var(--fg-1)",
          fontWeight: 400,
          fontSize: "30px",
          lineHeight: "32px",
        }}
      >
        Connect your agent
      </h1>

      {/* The bars carry position visually; the role and label are what say it out loud,
          since the design has no "Step 2 of 3" text to read. */}
      <div
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={WIZARD_STEPS.length}
        aria-valuenow={step + 1}
        aria-valuetext={`Step ${step + 1} of ${WIZARD_STEPS.length}: ${WIZARD_STEPS[step]}`}
        className="mt-[26px] flex items-center gap-1.5"
      >
        {WIZARD_STEPS.map((name, i) => (
          <span
            key={name}
            className="h-[2px] flex-1 rounded-full"
            style={{
              background: i <= step ? "var(--accent)" : "var(--onb-track)",
              transition: "background var(--dur-base, 240ms) ease",
            }}
          />
        ))}
      </div>

      {/* A heading, not a paragraph: it is what changes between steps, so it is what a
          screen reader navigating by heading needs to land on. */}
      <h2
        className="mt-[5px] text-[18px]"
        style={{ color: "var(--fg-1)", fontWeight: 600 }}
      >
        {WIZARD_STEPS[step]}
      </h2>

      <div className="mt-1.5 t-body-sm">{lead}</div>

      <div className="mt-5 flex flex-col gap-4">{children}</div>
    </div>
  );
}
